import { streamUrl } from "@/lib/api";
import { Play, Youtube, Image as ImageIcon } from "lucide-react";

/**
 * Grid-tile thumbnail used by Discover + Profile grids (posts / saved / liked).
 *
 * Renders the correct preview element per video kind so we never hit the
 * stream endpoint for something that isn't a real MP4:
 *   • Embeds     → <img src={embed_thumbnail_url}> + ▶ play badge in the
 *                  corner. YouTube-branded when embed_source === "youtube",
 *                  neutral for others. Prevents the 404-buffer-forever bug.
 *   • Photo posts→ <img src={thumbnail_url | cover_url}> + small photo icon
 *                  chip so users know the tile is a swipeable carousel.
 *   • Videos     → <video src={streamUrl(id)} preload="metadata" muted>.
 *
 * Consumers get a single component instead of copy-pasting the branch tree.
 */
export default function GridTilePreview({ video: v }) {
  if (v.is_embed) {
    const thumb = v.embed_thumbnail_url || v.thumbnail_url || "";
    const isYouTube = v.embed_source === "youtube";
    return (
      <>
        <img
          src={thumb}
          alt={v.embed_title || v.caption || "embed"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
        />
        {/* ▶ Play badge — sits top-left so it doesn't collide with the
            engagement stats we render bottom-left/right in every grid. */}
        <div
          data-testid={`tile-embed-badge-${v.id}`}
          className={`absolute top-1.5 left-1.5 h-6 px-1.5 flex items-center gap-1 border ${
            isYouTube
              ? "bg-[#FF0000]/90 border-[#FF0000] text-white"
              : "bg-black/80 border-white/30 text-white"
          }`}
        >
          {isYouTube ? (
            <Youtube className="h-3 w-3" fill="currentColor" strokeWidth={0} />
          ) : (
            <Play className="h-2.5 w-2.5 ml-0.5" fill="currentColor" strokeWidth={0} />
          )}
          <span className="font-mono text-[9px] uppercase tracking-widest font-bold">
            {isYouTube ? "yt" : v.embed_source || "embed"}
          </span>
        </div>
      </>
    );
  }
  if (v.is_photo_post) {
    return (
      <>
        <img
          src={v.thumbnail_url || v.cover_url || ""}
          alt={v.caption || "photo"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
        />
        <div className="absolute top-1.5 left-1.5 h-6 px-1.5 flex items-center gap-1 border bg-black/80 border-white/30 text-white">
          <ImageIcon className="h-3 w-3" strokeWidth={2} />
          <span className="font-mono text-[9px] uppercase tracking-widest font-bold">
            photo
          </span>
        </div>
      </>
    );
  }
  return (
    <video
      src={streamUrl(v.id)}
      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
      muted
      playsInline
      preload="metadata"
    />
  );
}
